import { Component} from '@angular/core';
import { ServicemanagementService } from '../../services/servicemanagement.service';
import { ClientSelectionService } from '../clients/client-selection.service';
import { distinctUntilChanged, finalize, map, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AddservicedialogComponent } from './addservicedialog/addservicedialog.component';
import {AddServiceDialogResult} from '../../models/addservicedialog.model';
import { DeleteconfirmationdialogComponent, DeleteConfirmationData } from '../deleteconfirmationdialog/deleteconfirmationdialog.component';
import { ServiceRow, GetServicesResponse } from '../../models/servicemanagement.model';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';

@Component({
  selector: 'app-servicemanagement',
  standalone: false,
  templateUrl: './servicemanagement.component.html',
  styleUrls: ['./servicemanagement.component.css'],
})
export class ServicemanagementComponent extends BaseTableComponent<ServiceRow> {
  readonly displayedColumns: string[] = ['serviceName', 'serviceDescription', 'startDate', 'actions'];

  clientId?: number;
  clientName?: string;
  deletingKey?: string;
  override sortBy = 'serviceName';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private serviceManagementService: ServicemanagementService,
    private clientSelection: ClientSelectionService,
    private dialog: MatDialog
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resolveClientId();
  }

  addService(): void {
    if (!this.clientId) return;
    this.loading = true;
    this.errorMessage = '';
    this.fetchAssignedServiceNames(this.clientId)
      .pipe(finalize(() => { this.loading = false; }))
      .subscribe({
        next: (existingServiceNames) => {
          const ref = this.dialog.open<AddservicedialogComponent, { clientId: number; existingServiceNames: string[] }, AddServiceDialogResult>(AddservicedialogComponent, {
            width: '520px',
            disableClose: true,
            data: {
              clientId: this.clientId!,
              existingServiceNames,
            }
          });

          ref.componentInstance.submitRequest.subscribe((result) => {
            const payload: ServiceRow = {
              ClientID: this.clientId,
              ServiceName: result.serviceName.trim(),
              StartDate: result.startDate ?? null
            };

            this.loading = true;
            this.serviceManagementService
              .addService(payload)
              .pipe(finalize(() => { this.loading = false; }))
              .subscribe({
                next: (res) => {
                  if (res?.success) {
                    ref.close();
                    this.pageIndex = 0;
                    this.loadDataWrapper();
                  } else {
                    ref.componentInstance.setDialogError('Failed to add service');
                  }
                },
                error: (err) => {
                  ref.componentInstance.setDialogError(formatHttpErrorMessage('Failed to add service', err));
                },
              });
          });
        },
        error: (err) => {
          this.errorMessage = `Failed to load services${err?.status ? ` (HTTP ${err.status})` : ''}.`;
        },
      });
  }

  remove(row: ServiceRow): void {
    if (!this.clientId || !row?.ServiceName) return;
    
    const dialogData: DeleteConfirmationData = {
      title: 'Delete Service',
      message: `Are you sure you want to remove service ${row.ServiceName} for client ${this.clientName || this.clientId}?`
    };

    const dialogRef = this.dialog.open<DeleteconfirmationdialogComponent, DeleteConfirmationData, boolean>(
      DeleteconfirmationdialogComponent,
      {
        width: '400px',
        data: dialogData,
        autoFocus: false,
        restoreFocus: false
      }
    );

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      
      this.deletingKey = `${row.ServiceName}`;
      this.serviceManagementService
        .deleteService({ ClientID: this.clientId, ServiceName: row.ServiceName })
        .pipe(finalize(() => (this.deletingKey = undefined)))
        .subscribe({
          next: (res) => {
            if (res?.success) {
              this.loadDataWrapper();
            } else {
              this.errorMessage = 'Delete failed: server returned success=false.';
            }
          },
          error: (err) => {
            this.errorMessage = `Delete failed${err?.status ? ` (HTTP ${err.status})` : ''}.`;
          },
        });
    });
  }

  isDeletingRow(row: ServiceRow): boolean {
    return this.deletingKey === `${row.ServiceName}`;
  }

  protected override loadData(skip: number, count: number): void {
    if (!this.clientId) return;
    this.loading = true;
    this.errorMessage = '';
    this.serviceManagementService
      .getServices(this.clientId, this.sortBy, this.sortDir, skip, count, this.searchTerm)
      .pipe(finalize(() => { this.loading = false; }))
      .subscribe({
        next: (resp: GetServicesResponse) => {
          this.dataSource.data = resp.services ?? [];
          this.totalCount = resp.totalCount ?? this.dataSource.data.length;
        },
        error: (err) => {
          this.errorMessage = `Failed to fetch services${err?.status ? ` (HTTP ${err.status})` : ''}.`;
        },
      });
  }

  private fetchAssignedServiceNames(clientId: number) {
    const count = Math.max(this.totalCount, this.pageSize, 1);

    return this.serviceManagementService
      .getServices(clientId, 'serviceName', 'asc', 0, count)
      .pipe(
        map((response: GetServicesResponse) =>
          (response.services ?? [])
            .map((service) => service.ServiceName)
            .filter((serviceName): serviceName is string => !!serviceName)
        )
      );
  }

  private resolveClientId(): void {
    this.clientSelection.selectedClient$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((client) => {
        this.clientId = client?.ClientID ?? undefined;
        this.clientName = client?.DisplayName ?? undefined;
        if (this.clientId != null) {
          this.loadDataWrapper();
        } else {
          this.dataSource.data = [];
          this.totalCount = 0;
          this.errorMessage = 'Please select a client to view services.';
        }
      });
  }
}